"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api-client";
import type { AnyDetailedListing } from "@/lib/types";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

export type ListingArchiveUnarchiveActionProps = {
  listing: AnyDetailedListing;
  trigger: ReactNode;
  onArchivedChanged?: (listing: AnyDetailedListing) => void | Promise<void>;
};

export function ListingArchiveUnarchiveAction({
  listing,
  trigger,
  onArchivedChanged,
}: ListingArchiveUnarchiveActionProps) {
  const t = useTranslations();

  const archiveMutation = api.listings.archive.useMutation();
  const unarchiveMutation = api.listings.unarchive.useMutation();

  async function handleConfirm() {
    const res =
      listing.status === "ARCHIVED"
        ? await unarchiveMutation.mutateAsync({ id: listing.id })
        : await archiveMutation.mutateAsync({ id: listing.id });

    onArchivedChanged?.(res.listing);
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(
              listing.status === "ARCHIVED"
                ? "saas.listing.dialog.unarchive.title"
                : "saas.listing.dialog.archive.title",
            )}
          </AlertDialogTitle>

          <AlertDialogDescription>
            {t(
              listing.status === "ARCHIVED"
                ? "saas.listing.dialog.unarchive.description"
                : "saas.listing.dialog.archive.description",
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel>
            {t(
              listing.status === "ARCHIVED"
                ? "saas.listing.dialog.unarchive.cancel"
                : "saas.listing.dialog.archive.cancel",
            )}
          </AlertDialogCancel>

          <AlertDialogAction onClick={handleConfirm}>
            {t(
              listing.status === "ARCHIVED"
                ? "saas.listing.dialog.unarchive.confirm"
                : "saas.listing.dialog.archive.confirm",
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

ListingArchiveUnarchiveAction.displayName = "ListingArchiveUnarchiveAction";
